import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * AL CAMBIAR DE PAGINA, ARRIBA. Menos cuando se vuelve a un sitio donde el
 * lector ya estaba (Faina, 1-sep: «cuando vuelvo atrás no me deja en el paso
 * anterior»).
 *
 * 🔴 ESTE COMPONENTE ERA LA MITAD DEL PROBLEMA: aunque la biblioteca
 * recuperara la búsqueda, los filtros y la página, este `scrollTo(0, 0)` dejaba
 * al lector arriba del todo. Con 5.880 fichas eso significa buscar a mano por
 * dónde ibas.
 *
 * La biblioteca apunta la posición al abrir una pieza y la repone al volver;
 * aquí solo hay que no estorbar. La marca es `state.recuperarY`.
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    /* La biblioteca repone su propia altura al volver (ver BibliotecaPage);
       aqui solo hay que no pisarsela. */
    if (pathname === '/canalizaciones') return undefined;

    /* 🔗 Y SI EL ENLACE PIDE UN SITIO CONCRETO, SE VA AHI (1-sep). Un enlace
       con almohadilla —`/conservacion#hablemos`— prometia llevar a una seccion
       y este `scrollTo(0, 0)` lo dejaba arriba del todo, asi que el enlace
       parecia roto aunque funcionase.

       ⚠️ Y NO BASTA CON SALTAR UNA VEZ, NI TRES. Medido en Conservacion: tras
       el salto la pagina se sigue moviendo sola durante mas de un segundo
       —fuentes que entran, imagenes que reservan su hueco, el anclaje de scroll
       del navegador ajustando— y el destino acababa 800 px por encima de la
       ventana. Con tres intentos escalonados seguia cayendo mal.

       Asi que se CORRIGE HASTA QUE PARA: mientras la seccion no este donde
       toca, se vuelve a colocar, y se deja de insistir al segundo y medio o en
       cuanto el lector toca la rueda —si el ha decidido mirar otra cosa, la
       pagina es suya—.

       El destino son 90 px desde arriba, que es lo que deja `:target`
       (scroll-margin-top) para que no lo tape el menu fijo. */
    if (hash) {
      /* 🔴 EL SALTO SE MANTIENE HASTA QUE LA PAGINA TERMINA DE CARGAR. Medido:
         el destino queda bien colocado al instante y luego se va yendo —91,
         291, 491… hasta 991 px— porque lo que hay ENCIMA sigue creciendo
         mientras entran las tipografias de lectura, los textos traducidos y las
         imagenes. Quien pulsaba el enlace veia el sitio correcto medio segundo
         y despues se encontraba en otra parte.

         Un temporizador fijo no sirve: no se sabe cuanto tarda, y pasarse
         significa dar tirones cuando el lector ya esta leyendo. Se escucha el
         TAMAÑO de la pagina —que es justo lo que cambia— y se recoloca cada vez
         que cambia, hasta que la pagina esta cargada del todo: `load` y las
         tipografias listas, mas un respiro.

         SE PARA ANTES en cuanto el lector toca la rueda, la pantalla o una
         tecla: si el ha decidido mirar otra cosa, la pagina es suya. Y en
         cualquier caso a los ocho segundos, para no dejar nada escuchando. */
      let vivo = true;
      const soltar = () => { vivo = false; };
      ['wheel', 'touchstart', 'keydown'].forEach(
        (e) => window.addEventListener(e, soltar, { passive: true, once: true }));

      const colocar = () => {
        if (!vivo) return;
        const destino = document.querySelector(hash);
        /* Los 90 px los pone `.ancla` con `scroll-margin-top`, para que el
           menu fijo no tape el rotulo de la seccion. */
        if (destino && Math.abs(destino.getBoundingClientRect().top - 90) > 4) {
          destino.scrollIntoView({ block: 'start' });
        }
      };

      colocar();
      const vigia = new ResizeObserver(colocar);
      vigia.observe(document.body);

      /* Se suelta cuando se cumplen LAS DOS cosas: que la pagina haya
         terminado de cargar y que haya pasado un minimo de tiempo.

         ⚠️ EL MINIMO NO SOBRA. Al llegar desde otra pagina del sitio —un clic
         en un enlace, sin recarga— la pagina YA esta «cargada» y las
         tipografias YA estan listas, asi que sin el minimo se soltaba al
         instante y el destino volvia a irse: medido, acababa 511 px por debajo
         en vez de a 90. Lo que crece entonces no es la carga, es el propio
         React montando la pagina nueva. Con 2,5 s la portada aun se movia
         despues —acababa 66 px pasada—; con 4 s se queda quieta. */
      let cargada = false;
      let esperado = false;
      const soltarSiToca = () => {
        if (!cargada || !esperado) return;
        vivo = false;
        vigia.disconnect();
      };
      const minimo = setTimeout(() => { esperado = true; soltarSiToca(); }, 4000);
      const finCarga = document.readyState === 'complete'
        ? Promise.resolve()
        : new Promise((r) => window.addEventListener('load', r, { once: true }));
      const letras = document.fonts ? document.fonts.ready : Promise.resolve();
      Promise.all([finCarga, letras]).then(() => { cargada = true; soltarSiToca(); });
      const tope = setTimeout(() => { vivo = false; vigia.disconnect(); }, 8000);

      return () => {
        vivo = false;
        vigia.disconnect();
        clearTimeout(minimo);
        clearTimeout(tope);
        ['wheel', 'touchstart', 'keydown'].forEach(
          (e) => window.removeEventListener(e, soltar));
      };
    }

    window.scrollTo(0, 0);
    return undefined;
  }, [pathname, hash]);

  return null;
}

export default ScrollToTop;
